import React, { useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { readStoredUser } from "../../../utils/sessionUser";
import { usePmsTheme } from "../../../Components/PmsThemeContext";
import OriginalDocumentViewer from "./OriginalDocumentViewer";
import { OriginalDocumentDialogScopeContext } from "./OriginalDocumentDialogScope";
import { originalViewerText } from "./originalViewerMessages";
import "./OriginalDocumentViewer.css";

export function useOriginalDocument(isOpen = true, scopeKey) {
  const [original, setOriginal] = useState(null);
  const trigger = useRef(null);
  const close = useCallback(() => {
    setOriginal(null);
    if (trigger.current?.isConnected) trigger.current.focus();
    trigger.current = null;
  }, []);
  useEffect(() => { close(); }, [isOpen, scopeKey, close]);
  const open = useCallback((id, name) => {
    const user = readStoredUser();
    if (!id || !user?.active_business_unit_id) return;
    trigger.current = document.activeElement;
    setOriginal({ id, name, businessUnitId: Number(user.active_business_unit_id), userId: user.id });
  }, []);
  return { original, open, close };
}

// Keep the form in the same React position when opening, closing or detaching.
// In particular, unsaved OCR edits must never be reset by opening the original.
export default function OriginalDocumentLayout({ original, onClose, children, dialogs, page = false }) {
  const { languageId } = usePmsTheme();
  const layoutRef = useRef(null);
  const mainRef = useRef(null);
  const inheritedScope = useContext(OriginalDocumentDialogScopeContext);
  const [portalTarget] = useState(() => {
    const target = document.createElement("div");
    target.className = "original-layout__dialogs";
    return target;
  });
  useLayoutEffect(() => {
    mainRef.current.appendChild(portalTarget);
    return () => portalTarget.remove();
  }, [portalTarget]);
  const allowsFocus = useCallback((target) => {
    const viewer = layoutRef.current?.querySelector(":scope > .original-viewer");
    return Boolean(target?.nodeType && viewer?.contains(target));
  }, []);
  const isSplit = Boolean(original);
  const hasDialogScope = dialogs !== undefined;
  const scope = useMemo(() => !hasDialogScope ? inheritedScope : {
    portalTarget, allowsFocus, isSplit,
  }, [hasDialogScope, inheritedScope, portalTarget, allowsFocus, isSplit]);
  const modal = Boolean(original) && !page;
  return <OriginalDocumentDialogScopeContext.Provider value={scope}>
    <div ref={layoutRef} className={`original-layout${original ? " original-layout--open" : ""}${page ? " original-layout--page" : ""}${hasDialogScope ? " original-layout--dialogs" : ""}`}
    role={modal ? "dialog" : undefined} aria-modal={modal || undefined}
    aria-label={modal ? originalViewerText(languageId, "Document viewer") : undefined}>
    <div className="original-layout__main" ref={mainRef}>
      <div className="original-layout__content">{children}</div>
      {createPortal(dialogs || null, portalTarget)}
    </div>
    {original && <OriginalDocumentViewer key={`${original.businessUnitId}:${original.documentType || "ocr"}:${original.invoiceId || ""}:${original.id}`} original={original} onClose={onClose} />}
    </div>
  </OriginalDocumentDialogScopeContext.Provider>;
}
